// La recherche universelle : la même dans toutes les pages du CRM.
//
// Une seule règle à retenir pour l'utilisateur : « je tape ce dont je me
// souviens, dans l'ordre que je veux, sans accent, et ça trouve ».
//
// Les règles, dans l'ordre :
//   1. ACCENTS, CASSE ET PONCTUATION IGNORÉS — « mees » trouve « Mées ».
//   2. CHAQUE MOT TAPÉ DOIT ÊTRE RETROUVÉ, peu importe où et dans quel ordre :
//      « dupont toiture » et « toiture dupont » donnent la même liste. Taper un
//      mot de plus AFFINE la recherche au lieu de l'élargir.
//   3. UN MOT DE CHIFFRES cherche aussi dans les numéros collés : « 0612 »
//      trouve « 06 12 34 56 78 », « 250012 » trouve « DV-2025-0012 ».
//
// Fonctions PURES : aucun accès réseau ni base, donc testables ligne à ligne.

import { chiffresSeuls, motsRecherche, normaliserTexte } from './recherche-client';

export { chiffresSeuls, motsRecherche, normaliserTexte };

/**
 * Un champ cherchable : du texte, un nombre, ou rien du tout — les champs
 * vides de la base (null) passent donc sans précaution particulière.
 */
export type ChampCherchable = string | number | null | undefined;

/** Le texte d'une ligne, prêt à être comparé (normalisé + chiffres collés). */
export interface TexteIndexe {
  /** Tous les champs mis bout à bout, sans accent ni casse. */
  texte: string;
  /** Les seuls chiffres de la ligne, collés : téléphones, numéros de pièce. */
  chiffres: string;
}

/**
 * Prépare une ligne pour la recherche à partir de ses champs affichés.
 * Les valeurs vides (null, undefined, nombres) sont acceptées telles quelles.
 */
export function indexerTexte(champs: readonly ChampCherchable[]): TexteIndexe {
  const brut = champs.map((v) => (typeof v === 'string' ? v : (v?.toString() ?? ''))).join(' ');
  return { texte: normaliserTexte(brut), chiffres: chiffresSeuls(brut) };
}

/** Vrai si ce mot (déjà normalisé) se retrouve dans la ligne. */
function motTrouve(index: TexteIndexe, mot: string): boolean {
  if (index.texte.includes(mot)) return true;
  // « 250012 » doit retrouver « DV-2025-0012 » : on compare chiffres à chiffres.
  return /^\d+$/.test(mot) && index.chiffres.includes(mot);
}

/** Vrai si la ligne répond à la saisie ; une saisie vide accepte tout. */
export function correspond(champs: readonly ChampCherchable[], saisie: string): boolean {
  const mots = motsRecherche(saisie);
  if (mots.length === 0) return true;
  const index = indexerTexte(champs);
  return mots.every((mot) => motTrouve(index, mot));
}

/**
 * Les lignes qui correspondent, dans leur ordre d'origine.
 *
 * `champsDe` rend ce qui est cherchable sur une ligne — en pratique ce que
 * l'utilisateur voit à l'écran, pour qu'aucune recherche ne reste inexpliquée.
 * Une saisie vide rend la liste inchangée : le champ de recherche ne masque
 * jamais rien tant qu'on n'a rien tapé.
 */
export function filtrerRecherche<T>(
  lignes: readonly T[],
  saisie: string,
  champsDe: (ligne: T) => readonly ChampCherchable[],
): T[] {
  const mots = motsRecherche(saisie);
  if (mots.length === 0) return [...lignes];
  return lignes.filter((ligne) => {
    const index = indexerTexte(champsDe(ligne));
    return mots.every((mot) => motTrouve(index, mot));
  });
}
